import React, { useState, useEffect } from "react";
import {
  TextField,
  Autocomplete,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Box,
  Tabs,
  Tab,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import Preview from "./Preview";

const fieldSx = {
  "& .MuiInputLabel-root": {
    fontSize: "0.72rem",
  },
  "& .MuiInputBase-root": {
    backgroundColor: "#fff",
    minHeight: 38,
    height: 38,
    fontSize: "0.72rem",
  },
  "& .MuiInputBase-input": {
    py: 0.65,
    px: 1,
    fontSize: "0.72rem",
  },
  "& .MuiAutocomplete-input": {
    minWidth: "0 !important",
  },
  "& .MuiFormHelperText-root": {
    fontSize: "0.58rem",
    lineHeight: 1.05,
    marginLeft: "2px",
    marginRight: "2px",
    marginTop: "2px",
  },
};

const StimRow = ({
  index,
  stim={},
  adAssetTypes,
  adTypeGroups,
  adTypes,
  brands,
  languages,
  locations,
  isSuperUser,
  onFieldChange,
  onDelete,
  onCreateBrand,
  applyToAll,
  setApplyToAll,
  uploadListLength,
  handleApplyToAll,
  errors = {},
}) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewTab, setPreviewTab] = useState(0); 

  const [editingTag, setEditingTag] = useState(
    stim.scriptData || stim.vastUrl || ""
  );
  const [editError, setEditError] = useState("");

  const [adAssetInput, setAdAssetInput] = useState("");
  const [adTypeGroupInput, setAdTypeGroupInput] = useState("");
  const [adTypeInput, setAdTypeInput] = useState("");
  const [brandInput, setBrandInput] = useState("");

  const [languageInput, setLanguageInput] = useState("");
  const [countryInput, setCountryInput] = useState("");

 useEffect(() => {
    if (isPreviewOpen) {
      setEditingTag(stim.scriptData || stim.vastUrl || "");
      setEditError("");
    }
  }, [isPreviewOpen, stim.scriptData, stim.vastUrl]);

  const isScriptPreview = !!stim.scriptData;
  const liveScriptData = isScriptPreview ? editingTag : "";
  const liveVastUrl = !isScriptPreview ? editingTag : "";
  const liveSelectedType = isScriptPreview ? "s" : "va";
  const liveTag = editingTag || "";

  const handleTagChange = (e) => {
    setEditingTag(e.target.value);
    if (e.target.value.trim() !== "") setEditError("");
  };

  const handleSaveTag = () => {
    const hasEditableText = stim.scriptData || stim.vastUrl;

    if (hasEditableText && editingTag.trim() === "") {
      setEditError("This field cannot be empty.");
      return;
    }

    onFieldChange(index, {
      ...(stim.scriptData ? { scriptData: editingTag } : {}),
      ...(stim.vastUrl ? { vastUrl: editingTag } : {}),
    });

    setEditError("");
    setIsPreviewOpen(false);
  };



  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns:
             "42px minmax(90px,0.9fr) minmax(100px,1fr) minmax(100px,1fr) minmax(90px,0.9fr) minmax(115px,1fr) minmax(110px,1fr) minmax(110px,1fr) 70px 34px",
          gap: 0.75,
          alignItems: "start",
          px: 0.75,
          py: 0.75,
          borderBottom: "1px solid #f0f0f0",
          backgroundColor: "#fff",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", pt: 0.2 }}>
          <Tooltip title="Open preview">
            <Box
             onClick={() => {
              setPreviewTab(0);
              setIsPreviewOpen(true);
            }}
              sx={{
                width: 28,
                height: 28,
                border: "1px solid #dcdcdc",
                borderRadius: "4px",
                backgroundColor: "#fff",
                overflow: "hidden",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "&:hover": {
                  borderColor: "#00badb",
                },
                "& img, & video": {
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                },
              }}
            >
              {stim.preview ? (
                stim.file?.type?.startsWith("video") ? (
                  <video src={stim.preview} />
                ) : (
                  <img src={stim.preview} alt="preview" />
                )
              ) : (
                <Box sx={{ fontSize: "0.5rem", color: "#777" }}>Open</Box>
              )}
            </Box>
          </Tooltip>
        </Box>

        <TextField
          label="Name"
          value={stim.name || ""}
          onChange={(e) => onFieldChange(index, { name: e.target.value })}
          error={!!errors.name}
          helperText={errors.name}
          required
          fullWidth
          size="small"
          sx={fieldSx}
        />

        <Box sx={{ display: "flex", alignItems: "flex-start" }}>
          <Autocomplete
            options={adAssetTypes || []}
            getOptionLabel={(option) => option?.name || ""}
            value={
              (adAssetTypes || []).find((item) => item.id === stim.adAssetTypeId) || null
            }
            inputValue={adAssetInput}
            onInputChange={(e, value) => setAdAssetInput(value)}
            onChange={(e, newVal) => {
              onFieldChange(index, { adAssetTypeId: newVal ? newVal.id : "" });
              setAdAssetInput(newVal?.name || "");
            }}
            fullWidth
            size="small"
            renderOption={(props, option) => (
              <li {...props} key={option.id} style={{ fontSize: "0.76rem" }}>
                {option.name}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Asset Type"
                error={!!errors.adAssetTypeId}
                helperText={errors.adAssetTypeId}
                required
                size="small"
                sx={fieldSx}
              />
            )}
          />
          {index === 0 && uploadListLength > 1 && (
            <Tooltip title="Apply to all">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={applyToAll.adAssetTypeId}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setApplyToAll((prev) => ({ ...prev, adAssetTypeId: checked }));
                      if (checked && stim.adAssetTypeId) {
                        handleApplyToAll("adAssetTypeId", stim.adAssetTypeId);
                      }
                    }}
                    size="small"
                    sx={{ p: 0.1 }}
                  />
                }
                label=""
                sx={{ ml: 0.05, mr: 0, mt: 0.05 }}
              />
            </Tooltip>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "flex-start" }}>
          <Autocomplete
            options={adTypeGroups || []}
            getOptionLabel={(option) => option?.name || ""}
            value={
              (adTypeGroups || []).find((item) => item.id === stim.adTypeGroupId) || null
            }
            inputValue={adTypeGroupInput}
            onInputChange={(e, value) => setAdTypeGroupInput(value)}
            onChange={(e, newVal) => {
              onFieldChange(index, { adTypeGroupId: newVal ? newVal.id : "" });
              setAdTypeGroupInput(newVal?.name || "");
            }}
            fullWidth
            size="small"
            renderOption={(props, option) => (
              <li {...props} key={option.id} style={{ fontSize: "0.76rem" }}>
                {option.name}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Type Group"
                error={!!errors.adTypeGroupId}
                helperText={errors.adTypeGroupId}
                required
                size="small"
                sx={fieldSx}
              />
            )}
          />
          {index === 0 && uploadListLength > 1 && (
            <Tooltip title="Apply to all">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={applyToAll.adTypeGroupId}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setApplyToAll((prev) => ({ ...prev, adTypeGroupId: checked }));
                      if (checked && stim.adTypeGroupId) {
                        handleApplyToAll("adTypeGroupId", stim.adTypeGroupId);
                      }
                    }}
                    size="small"
                    sx={{ p: 0.1 }}
                  />
                }
                label=""
                sx={{ ml: 0.05, mr: 0, mt: 0.05 }}
              />
            </Tooltip>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "flex-start" }}>
          <Autocomplete
            options={adTypes || []}
            getOptionLabel={(option) => option?.name || ""}
            value={(adTypes || []).find((item) => item.id === stim.adTypeId) || null}
            inputValue={adTypeInput}
            onInputChange={(e, value) => setAdTypeInput(value)}
            onChange={(e, newVal) => {
              onFieldChange(index, { adTypeId: newVal ? newVal.id : "" });
              setAdTypeInput(newVal?.name || "");
            }}
            fullWidth
            size="small"
            renderOption={(props, option) => (
              <li {...props} key={option.id} style={{ fontSize: "0.76rem" }}>
                {option.name}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Type"
                error={!!errors.adTypeId}
                helperText={errors.adTypeId}
                required
                size="small"
                sx={fieldSx}
              />
            )}
          />
          {index === 0 && uploadListLength > 1 && (
            <Tooltip title="Apply to all">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={applyToAll.adTypeId}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setApplyToAll((prev) => ({ ...prev, adTypeId: checked }));
                      if (checked && stim.adTypeId) {
                        handleApplyToAll("adTypeId", stim.adTypeId);
                      }
                    }}
                    size="small"
                    sx={{ p: 0.1 }}
                  />
                }
                label=""
                sx={{ ml: 0.05, mr: 0, mt: 0.05 }}
              />
            </Tooltip>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.35 }}>
          <Box sx={{ flex: 1 }}>
            <Autocomplete
              options={brands || []}
              getOptionLabel={(option) => option?.name || ""}
              value={(brands || []).find((item) => item.id === stim.brandId) || null}
              inputValue={brandInput}
              onInputChange={(e, value) => setBrandInput(value)}
              onChange={(e, newVal) => {
                onFieldChange(index, { brandId: newVal ? newVal.id : "" });
                setBrandInput(newVal?.name || "");
              }}
              fullWidth
              size="small"
              renderOption={(props, option) => (
                <li {...props} key={option.id} style={{ fontSize: "0.76rem" }}>
                  {option.name}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Brand"
                  error={!!errors.brandId}
                  helperText={errors.brandId}
                  required
                  size="small"
                  sx={fieldSx}
                />
              )}
            />
          </Box>

          {isSuperUser && (
            <Tooltip title="Create Brand">
              <Button
                onClick={onCreateBrand}
                sx={{
                  minWidth: 24,
                  width: 24,
                  height: 24,
                  mt: "7px",
                  backgroundColor: "#00badb",
                  color: "#fff",
                  fontWeight: "bold",
                  p: 0,
                  "&:hover": {
                    backgroundColor: "#00a6c4",
                  },
                }}
              >
                <AddIcon sx={{ fontSize: 14 }} />
              </Button>
            </Tooltip>
          )}

          {index === 0 && uploadListLength > 1 && (
            <Tooltip title="Apply to all">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={applyToAll.brandId}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setApplyToAll((prev) => ({ ...prev, brandId: checked }));
                      if (checked && stim.brandId) {
                        handleApplyToAll("brandId", stim.brandId);
                      }
                    }}
                    size="small"
                    sx={{ p: 0.1 }}
                  />
                }
                label=""
                sx={{ ml: 0, mr: 0, mt: 0.05 }}
              />
            </Tooltip>
          )}
        </Box>
      <Box sx={{ display: "flex", alignItems: "flex-start" }}>
        <Autocomplete
          options={languages || []}
          getOptionLabel={(option) => option?.name || ""}
          value={(languages || []).find((item) => item.id === stim.languageId) || null}
          inputValue={languageInput}
          onInputChange={(e, value) => setLanguageInput(value)}
          onChange={(e, newVal) => {
            const newLanguageId = newVal ? newVal.id : "";
            onFieldChange(index, { languageId: newLanguageId });
            setLanguageInput(newVal?.name || "");

            if (index === 0 && applyToAll.languageId && newLanguageId) {
              handleApplyToAll("languageId", newLanguageId);
            }
          }}
          fullWidth
          size="small"
          renderOption={(props, option) => (
            <li {...props} key={option.id} style={{ fontSize: "0.76rem" }}>
              {option.name}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Language"
              error={!!errors.languageId}
              helperText={errors.languageId}
              required
              size="small"
              sx={fieldSx}
            />
          )}
        />

        {index === 0 && uploadListLength > 1 && (
          <Tooltip title="Apply to all">
            <FormControlLabel
              control={
                <Checkbox
                  checked={applyToAll.languageId}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setApplyToAll((prev) => ({ ...prev, languageId: checked }));
                    if (checked && stim.languageId) {
                      handleApplyToAll("languageId", stim.languageId);
                    }
                  }}
                  size="small"
                  sx={{ p: 0.1 }}
                />
              }
              label=""
              sx={{ ml: 0.05, mr: 0, mt: 0.05 }}
            />
          </Tooltip>
        )}
      </Box>

      <Box sx={{ display: "flex", alignItems: "flex-start" }}>
        <Autocomplete
          options={locations || []}
          getOptionLabel={(option) => option?.name || ""}
          value={(locations || []).find((item) => item.id === stim.countryId) || null}
          inputValue={countryInput}
          onInputChange={(e, value) => setCountryInput(value)}
          onChange={(e, newVal) => {
            const newCountryId = newVal ? newVal.id : "";
            onFieldChange(index, { countryId: newCountryId });
            setCountryInput(newVal?.name || "");

            if (index === 0 && applyToAll.countryId && newCountryId) {
              handleApplyToAll("countryId", newCountryId);
            }
          }}
          fullWidth
          size="small"
          renderOption={(props, option) => (
            <li {...props} key={option.id} style={{ fontSize: "0.76rem" }}>
              {option.name}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Country"
              error={!!errors.countryId}
              helperText={errors.countryId}
              required
              size="small"
              sx={fieldSx}
            />
          )}
        />

        {index === 0 && uploadListLength > 1 && (
          <Tooltip title="Apply to all">
            <FormControlLabel
              control={
                <Checkbox
                  checked={applyToAll.countryId}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setApplyToAll((prev) => ({ ...prev, countryId: checked }));
                    if (checked && stim.countryId) {
                      handleApplyToAll("countryId", stim.countryId);
                    }
                  }}
                  size="small"
                  sx={{ p: 0.1 }}
                />
              }
              label=""
              sx={{ ml: 0.05, mr: 0, mt: 0.05 }}
            />
          </Tooltip>
        )}
      </Box>

<Box
  sx={{
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pt: 0.25,
  }}
>
  <FormControlLabel
    control={
      <Checkbox
        checked={!!stim.clutter}
        onChange={(e) =>
          onFieldChange(index, { clutter: e.target.checked })
        }
        size="small"
      />
    }
    label=""
    sx={{ m: 0 }}
  />
</Box>
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "flex-start", 
        pl: 0.3,
      }}
    >
      <Tooltip title="Delete">
        <IconButton
          onClick={() => onDelete(index)}
          size="small"
          sx={{
            p: 0,
            fontSize: "16px",
            fontWeight: 700,
            color: "#666",
            mt: 1.3,
          }}
        >
          ×
        </IconButton>
      </Tooltip>
    </Box>
      </Box>

      <Dialog
        open={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ pb: 1 }}>Preview & Edit</DialogTitle>

        <Box sx={{ px: 3, pt: 1 }}>
          <Tabs
            value={previewTab}
            onChange={(e, newValue) => setPreviewTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab label="Preview" />
            <Tab label="Edit" />
          </Tabs>
        </Box>

        <DialogContent sx={{ pt: 3 }}>
          {previewTab === 0 && (
            <Box sx={{ textAlign: "center", minHeight: 420 }}>
              {stim.file ? (
                <>
                  {stim.file.type.startsWith("image") ? (
                    <img
                      src={stim.preview}
                      alt="preview"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "400px",
                        marginBottom: "10px",
                      }}
                    />
                  ) : (
                    <video
                      controls
                      style={{
                        maxWidth: "100%",
                        maxHeight: "400px",
                        marginBottom: "10px",
                      }}
                    >
                      <source src={stim.preview} type={stim.file.type} />
                    </video>
                  )}

                  <div style={{ marginTop: "20px" }}>
                    <Button
                      variant="contained"
                      component="label"
                      style={{ backgroundColor: "#00badb", color: "white" }}
                    >
                      Upload New File
                      <input
                        type="file"
                        hidden
                        accept="image/*,video/*"
                        onChange={(e) => {
                          const newFile = e.target.files?.[0];
                          if (newFile) {
                            const newPreview = URL.createObjectURL(newFile);
                            onFieldChange(index, {
                              file: newFile,
                              preview: newPreview,
                            });
                          }
                        }}
                      />
                    </Button>
                  </div>
                </>
              ) : (
              <Box
                sx={{
                  mt: 2,
                  width: "100%",
                  height: 420,
                  position: "relative",
                  overflow: "hidden",
                }}
              >
              <Preview
                selectedType={liveSelectedType}
                scriptData={liveScriptData}
                vastUrl={liveVastUrl}
                tag={liveTag}
              />
              </Box>
              )}
            </Box>
          )}

          {previewTab === 1 && (
            <Box sx={{ minHeight: 320 }}>
              {(stim.scriptData || stim.vastUrl) ? (
                <TextField
                  label={stim.scriptData ? "Edit Script" : "Edit VAST URL/XML"}
                  multiline
                  fullWidth
                  minRows={10}
                  value={editingTag}
                  onChange={handleTagChange}
                  error={!!editError}
                  helperText={editError}
                  sx={{ mt: 1 }}
                />
              ) : stim.file ? (
                <Box sx={{ textAlign: "center", mt: 4 }}>
                  <Button
                    variant="contained"
                    component="label"
                    style={{ backgroundColor: "#00badb", color: "white" }}
                  >
                    Upload New File
                    <input
                      type="file"
                      hidden
                      accept="image/*,video/*"
                      onChange={(e) => {
                        const newFile = e.target.files?.[0];
                        if (newFile) {
                          const newPreview = URL.createObjectURL(newFile);
                          onFieldChange(index, {
                            file: newFile,
                            preview: newPreview,
                          });
                        }
                      }}
                    />
                  </Button>
                </Box>
              ) : (
                <Box sx={{ textAlign: "center", mt: 4, color: "#777" }}>
                  Nothing to edit
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setIsPreviewOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveTag}
            style={{ backgroundColor: "#00badb", color: "#fff" }}
            disabled={!(stim.scriptData || stim.vastUrl)}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default StimRow;